/**
 * Draft Cache Service — IndexedDB
 *
 * Zero-dependency local cache for AI-generated document drafts.
 * Provides instant restore when users reopen a document modal.
 *
 * DB:    cs_draft_cache
 * Store: drafts
 * Key:   `${documentId}::${companyCin}`
 */

const DB_NAME = 'cs_draft_cache';
const DB_VERSION = 1;
const STORE_NAME = 'drafts';
// Cap the local cache so it can't grow unbounded and exhaust the IndexedDB quota.
const MAX_DRAFTS = 200;

/* ── helpers ──────────────────────────────────────────────────────── */

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('pageOrigin', 'pageOrigin', { unique: false });
        store.createIndex('savedAt', 'savedAt', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function withStore(mode, fn) {
  return openDB().then((db) => {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const store = tx.objectStore(STORE_NAME);
      const result = fn(store);

      tx.oncomplete = () => {
        db.close();
        resolve(result._value);      // set by request handlers below
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };

      // Handle IDBRequest results
      if (result instanceof IDBRequest) {
        result.onsuccess = () => {
          result._value = result.result;
        };
      }
    });
  });
}

/* ── build cache key ──────────────────────────────────────────────── */

// Sentinel for the "personal draft" path (no company attached). Exported so
// callers comparing against a cached entry's companyCin use the same literal.
export const NO_CIN_PLACEHOLDER = 'no-cin';

function cacheKey(documentId, companyCin) {
  return `${documentId}::${companyCin || NO_CIN_PLACEHOLDER}`;
}

/* ── public API ───────────────────────────────────────────────────── */

/**
 * Save (or overwrite) a draft in the local cache.
 *
 * @param {object} record
 * @param {string} record.documentId
 * @param {string} record.companyCin
 * @param {string} record.pageOrigin      'document-library' | 'resolutions' | 'minutes'
 * @param {string} record.docHtml
 * @param {string} record.docSource       'ai' | 'fallback' | 'preview' | 'user-edited'
 * @param {string} record.documentTitle
 * @param {string} record.companyName
 * @param {object} [record.minutesConfig] Full MinutesPage form state (minutes only)
 * @param {object} [record.draftMeta]     AI generation metadata
 */
export async function saveDraft(record) {
  try {
    const key = cacheKey(record.documentId, record.companyCin);
    const entry = {
      ...record,
      key,
      savedAt: Date.now(),
    };
    await withStore('readwrite', (store) => store.put(entry));
    pruneOldDrafts().catch(() => {});
  } catch (err) {
    // Silently degrade — backend persistence is the safety net
    console.warn('[draftCache] saveDraft failed:', err);
  }
}

/**
 * Bound the cache: keep only the newest MAX_DRAFTS entries (by savedAt),
 * deleting the oldest. Prevents unbounded IndexedDB growth → quota exhaustion
 * that would silently break every future saveDraft. Fire-and-forget.
 */
async function pruneOldDrafts() {
  try {
    const db = await openDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const countReq = store.count();
      countReq.onsuccess = () => {
        const excess = countReq.result - MAX_DRAFTS;
        if (excess > 0) {
          // savedAt index is ascending → the cursor visits the oldest first.
          let deleted = 0;
          store.index('savedAt').openCursor().onsuccess = (event) => {
            const cursor = event.target.result;
            if (cursor && deleted < excess) {
              cursor.delete();
              deleted += 1;
              cursor.continue();
            }
          };
        }
      };
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch (err) {
    console.warn('[draftCache] pruneOldDrafts failed:', err);
  }
}

/**
 * Retrieve a cached draft.
 * @returns {object|null}
 */
export async function getDraft(documentId, companyCin) {
  try {
    const key = cacheKey(documentId, companyCin);
    const result = await withStore('readonly', (store) => store.get(key));
    return result || null;
  } catch (err) {
    console.warn('[draftCache] getDraft failed:', err);
    return null;
  }
}

/**
 * Delete a specific cached draft.
 */
export async function deleteDraft(documentId, companyCin) {
  try {
    const key = cacheKey(documentId, companyCin);
    await withStore('readwrite', (store) => store.delete(key));
  } catch (err) {
    console.warn('[draftCache] deleteDraft failed:', err);
  }
}

/**
 * List all cached drafts (for offline fallback / diagnostics).
 * @returns {object[]}
 */
export async function listDrafts() {
  try {
    const result = await withStore('readonly', (store) => store.getAll());
    return result || [];
  } catch (err) {
    console.warn('[draftCache] listDrafts failed:', err);
    return [];
  }
}

/**
 * Purge the entire draft cache.
 */
export async function clearAllDrafts() {
  try {
    await withStore('readwrite', (store) => store.clear());
  } catch (err) {
    console.warn('[draftCache] clearAllDrafts failed:', err);
  }
}

/**
 * Cursor-walks the store and deletes every cached draft for the given CIN.
 * Called by AppContext.deleteCompany to evict stale-cache rows.
 */
export async function deleteByCompanyCin(companyCin) {
  if (!companyCin) return 0;
  try {
    let removed = 0;
    await new Promise((resolve, reject) => {
      openDB().then((db) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const cursorReq = store.openCursor();
        cursorReq.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            if (cursor.value && cursor.value.companyCin === companyCin) {
              cursor.delete();
              removed += 1;
            }
            cursor.continue();
          }
        };
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => { db.close(); reject(tx.error); };
      }).catch(reject);
    });
    return removed;
  } catch (err) {
    console.warn('[draftCache] deleteByCompanyCin failed:', err);
    return 0;
  }
}

const draftCacheService = {
  saveDraft,
  getDraft,
  deleteDraft,
  listDrafts,
  clearAllDrafts,
  deleteByCompanyCin,
};

export default draftCacheService;
